<template>
  <div>
    <div :class="$style.containerHeader">
      <div class="flex space-between">
        <h1 :class="$style.title">
          Criteria Overview
          <b-tooltip
            :active="true"
            :label="criteriaInfo"
            position="is-right"
            size="is-medium"
            multilined
          >
            <b-icon
              icon="information-outline"
              size="is-small"
              class="padding"
            ></b-icon>
          </b-tooltip>
        </h1>
        <a :class="$style.iconBox" @click="collapseSideNav()">
          <b-icon icon="chevron-double-left" size="is-medium"></b-icon>
        </a>
      </div>
      <div class="padding-vertical-12">
        <b-field class="grip-search-input-box">
          <b-input
            type="search"
            icon="magnify"
            placeholder="Search Criteria(s)"
            v-model="searchTitle"
            :disabled="!isGripAnalysis"
          >
          </b-input>
        </b-field>
      </div>
    </div>

    <BaseLoading v-if="isLoading" />
    <div v-else class="grip-existing-criteria-list">
      <div v-if="!itemList.length" class="null-container">
        <p>No criteria found.</p>
      </div>
      <div
        v-else
        v-for="(criteria, index) in itemList"
        :key="index"
        class="item-container"
      >
        <b-checkbox
          :native-value="criteria.id"
          v-model="currentCriteriaIds"
          @input="changeSelectedCriteria(currentCriteriaIds, criteria)"
          :class="[
            'align-reverse',
            !isGripAnalysis ? 'cursor-not-allowed' : null,
          ]"
          :disabled="!isGripAnalysis"
        >
          <div :class="$style.criteriaDetailBox">
            <p :class="$style.documentId">{{ criteria.documentId }}</p>
            <p :class="$style.title">{{ criteria.title }}</p>
          </div>
        </b-checkbox>
      </div>
    </div>

    <div class="columns level" :class="$style.row">
      <div class="level-left" />
      <div class="level-right padding-horizontal-12">
        <button
          class="button is-primary is-rounded"
          @click="handleAddCriteria()"
          :disabled="isButtonDisabled"
        >
          Update
        </button>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { ApiState } from '../../../store/types/general.types';
import { Debounce } from '../../../jbi-shared/util/debounce.vue-decorator';
import BaseLoading from '../../../components/base/BaseLoading.vue';
import { ProjectCriteria } from '../../../store/modules/projects/types/projects.types';

@Component({
  components: { BaseLoading },
})
export default class ExistingCriteriaList extends Vue {
  @Prop() public projectId!: number;
  @Prop() public analysisCriteriaIds!: number[];
  @Prop() public isGripAnalysis!: boolean;

  public currentCriteriaSelection: ProjectCriteria[] = [];
  public currentCriteriaIds: number[] = [];
  public isLoading: boolean = true;
  public searchTitle: string = '';
  public isButtonDisabled: boolean = true;
  public criteriaInfo: string =
    'This criteria list can be amended for use of GRiP Report only.';

  @Action('projects/getFilteredProjectCriteria')
  public getFilteredProjectCriteria!: (payload: {
    projectId: number;
    params: { title: string };
  }) => void;

  @State((state) => state.projects.projectCriteria)
  public projectCriteria!: ProjectCriteria[];

  @State((state) => state.projects.apiState.getProjectCriteria)
  public getProjectCriteriaApiState!: ApiState;

  get itemList(): ProjectCriteria[] {
    return this.projectCriteria ? this.projectCriteria : [];
  }

  public mounted(): void {
    this.getFilteredProjectCriteria({
      projectId: this.projectId,
      params: { title: this.searchTitle },
    });
  }

  public collapseSideNav(): void {
    this.$emit('collapseSideNav');
  }

  public changeSelectedCriteria(
    currentCriteriaIds: number[],
    projectCriteria: ProjectCriteria,
  ): void {
    if (currentCriteriaIds.includes(projectCriteria.id)) {
      this.currentCriteriaSelection.push(projectCriteria);
    } else {
      this.currentCriteriaSelection = this.currentCriteriaSelection.filter(
        (selectionCriteria) => selectionCriteria.id !== projectCriteria.id,
      );
    }

    /** Existing selected criteria of analysis */
    const analysisCriteriaIds: string = JSON.stringify(
      this.analysisCriteriaIds.sort((a, b) => a - b),
    );

    /** Current selectionof criteria */
    const selectedCriteria: string = JSON.stringify(
      this.currentCriteriaSelection
        .map((criteria) => criteria.id)
        .sort((a, b) => a - b),
    );

    /** Disabled based on criteria selection */
    this.isButtonDisabled =
      this.currentCriteriaSelection.length > 0 &&
      analysisCriteriaIds !== selectedCriteria
        ? false
        : true;
  }

  public handleAddCriteria(): void {
    const selectedCriteriaIds: number[] = [];
    this.projectCriteria.forEach((criteria) => {
      if (this.currentCriteriaIds.includes(criteria.id)) {
        selectedCriteriaIds.push(criteria.id);
      }
    });

    if (!this.isButtonDisabled) {
      this.$emit('updateSelectedCriteria', {
        selectedCriteriaIds,
        update: true,
      });
    }
  }

  @Watch('searchTitle')
  @Debounce(500)
  public onTitleSearch(): void {
    this.getFilteredProjectCriteria({
      projectId: this.projectId,
      params: { title: this.searchTitle },
    });
  }

  @Watch('getProjectCriteriaApiState.loading')
  public watchGetProjectCriteriaLoading(): void {
    this.isLoading = true;
  }

  @Watch('getProjectCriteriaApiState.success')
  public watchGetProjectCriteriaSuccess(): void {
    this.isLoading = false;
    if (this.analysisCriteriaIds && this.analysisCriteriaIds.length > 0) {
      this.analysisCriteriaIds.forEach((criteriaId) => {
        const criteria: ProjectCriteria | undefined = this.projectCriteria
          ? this.projectCriteria.find(
              (projectCriteria) => projectCriteria.id === criteriaId,
            )
          : undefined;

        if (criteria) {
          this.currentCriteriaIds.push(criteriaId);
          if (criteria) {
            this.changeSelectedCriteria(this.currentCriteriaIds, criteria);
          }
        }
      });
    }
    const selectedCriteriaIds: number[] = this.currentCriteriaSelection.map(
      (criteria) => criteria.id,
    );
    this.$emit('updateSelectedCriteria', {
      selectedCriteriaIds,
      update: false,
    });
  }
}
</script>
<style lang="scss" module>
.row {
  width: 100%;
  margin: 0 !important;
}
.containerHeader {
  border-bottom: 1px solid #dbdbdb;
  width: 100%;

  .title {
    font-weight: 700;
    font-size: 20px;
    line-height: 27px;
    color: #444f51;
    padding: 0.75rem 0;
  }

  .iconBox {
    font-weight: 700;
    font-size: 20px;
    line-height: 27px;
    color: #444f51;
    padding: 0.75rem 0;
  }
}

.criteriaDetailBox {
  width: 100%;
  padding: 1rem 0.5rem;
  border-bottom: 1px solid #dbdbdb;

  .title {
    overflow: hidden;
    font-weight: 600;
    font-size: 14px;
    line-height: 19px;
    color: #444f51;
  }

  .documentId {
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    color: #9f9f9f;
  }
}

.b-checkbox.checkbox .control-label {
  padding-left: 8px;
  font-weight: 700;
  width: 100%;
}
</style>
